import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Tree } from 'react-arborist';
import type { NodeRendererProps, RowRendererProps } from 'react-arborist';
import { AlertTriangle, ChevronDown, ChevronRight, Info, Move } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';

/**
 * `/settings/competency-hierarchy` (v0.5 ② タレントマネジメント階層構造化、工程⑩)。
 * 設計: docs/design/v05-competency-hierarchy-detail.md §5「UI設計」(D-758/D-759/D-799)。
 *
 * entity_type (業務/スキル/抽象度) ごとのページ内タブに「意味論の設定」「給与算入フラグ (D-788、
 * 業務/スキルのみ)」「ツリー UI (親の付け替え)」を並べ、整合設定 (D-731/D-742、entity_type を
 * 持たないテナント単位の設定) はページ上部に1箇所だけ置く。
 *
 * ツリーは `HierarchyPage.tsx` の react-arborist Tree パターンを流用するが、流用元が内包していた
 * 完了条件違反 (§5-3 の F-1〜F-10) は継承しない: chevron/行内ボタンの当たり判定を44pxにする、
 * rowHeight を44px以上にする、行内ボタンを常時可視+tabIndex=-1にする、text-brand-redを継承しない、
 * aria-level を二重付与しない (react-arborist が自動付与、不足するaria-posinset/aria-setsizeのみ
 * renderRow で合成する)、生パレット(bg-green-100等)を使わない。
 */

const MAX_TAXONOMY_DEPTH = 6;
type EntityType = 'business_function' | 'skill' | 'abstraction_level';
const ENTITY_TABS: { key: EntityType; label: string }[] = [
  { key: 'business_function', label: '業務' },
  { key: 'skill', label: 'スキル' },
  { key: 'abstraction_level', label: '抽象度' },
];
const AGGREGATION_LABELS: Record<'all_children' | 'required_only', string> = {
  all_children: '直系の子をすべて満たす (既定)',
  required_only: '必須指定の子のみを満たす',
};

type HierarchySetting = {
  entity_type: EntityType;
  expansion_enabled: boolean;
  prerequisite_enabled: boolean;
  aggregation_function: 'all_children' | 'required_only';
  pay_increment_includes_group_targets: boolean;
  max_depth: number;
  effective_from: string | null;
};
type HierarchySettingsResponse = {
  settings: HierarchySetting[];
  consistency_mode: 'warn' | 'enforce';
  consistency_mode_effective_from: string | null;
};
type TenantFeature = { feature_code: string; is_enabled: boolean };
type TaxonomyNode = { id: string; code: string; label: string; parent_id: string | null; is_required: boolean; edge_effective_from: string | null };
type TaxonomyResponse = { entity_type: EntityType; max_depth: number; nodes: TaxonomyNode[] };
type ConsistencyViolation = {
  child_skill_id: string; child_skill_label: string;
  parent_skill_id: string; parent_skill_label: string;
  child_business_function_id: string; child_business_function_label: string;
  parent_business_function_id: string; parent_business_function_label: string;
};
type SkillMasterRow = { skill_id: string; business_function_id: string | null };

// =============================================================================
// ツリー構築ヘルパー (HierarchyPage.tsx の flattenTree/collectSubtreeIds と同じパターン)
// =============================================================================
type TreeNode = TaxonomyNode & { children?: TreeNode[] };

const buildChildrenMap = (nodes: TaxonomyNode[]): Map<string | null, TaxonomyNode[]> => {
  const map = new Map<string | null, TaxonomyNode[]>();
  for (const n of nodes) {
    const key = n.parent_id;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(n);
  }
  return map;
};

/** ⚠️ 子が0件のノードは `children: undefined` にする (react-arborist の isLeaf は
 *  `!Array.isArray(children)` で判定するため、`[]` を渡すと空でも internal 扱いになり
 *  chevron 列が出てしまう、§5-3「子が0件のときは chevron 列を出さない」)。 */
const buildTaxonomyTree = (nodes: TaxonomyNode[]): TreeNode[] => {
  const byParent = buildChildrenMap(nodes);
  const build = (parentId: string | null): TreeNode[] =>
    (byParent.get(parentId) ?? []).map((n) => {
      const children = build(n.id);
      return { ...n, children: children.length > 0 ? children : undefined };
    });
  return build(null);
};

type FlatTaxonomyNode = { node: TaxonomyNode; path: string; depth: number };

const flattenForSelect = (nodes: TaxonomyNode[]): FlatTaxonomyNode[] => {
  const byParent = buildChildrenMap(nodes);
  const result: FlatTaxonomyNode[] = [];
  const walk = (parentId: string | null, prefix: string, depth: number) => {
    for (const n of byParent.get(parentId) ?? []) {
      const path = prefix ? `${prefix} / ${n.label}` : n.label;
      result.push({ node: n, path, depth });
      walk(n.id, path, depth + 1);
    }
  };
  walk(null, '', 0);
  return result;
};

/** 対象ノード自身とその子孫の id 集合 (移動先候補から除外するため) */
const collectSubtreeIds = (nodes: TaxonomyNode[], rootId: string): Set<string> => {
  const byParent = buildChildrenMap(nodes);
  const ids = new Set<string>([rootId]);
  const walk = (id: string) => { for (const c of byParent.get(id) ?? []) { ids.add(c.id); walk(c.id); } };
  walk(rootId);
  return ids;
};

/** §5-3-1: 深さ1〜3は20px/段、深さ4〜6は12px/段の逓減インデント (node.level = ルートからの hop 数)。 */
const computeIndent = (level: number): number => {
  let total = 0;
  for (let d = 1; d <= level; d++) total += d <= 3 ? 20 : 12;
  return total;
};

// =============================================================================
// ツリー行 (F-1〜F-10 の是正込み)
// =============================================================================
type TaxonomyRowProps = {
  node: NodeRendererProps<TreeNode>['node'];
  style: NodeRendererProps<TreeNode>['style'];
  faceCountByBusinessFunction: Map<string, number> | null;
  onChangeParent: (item: TaxonomyNode) => void;
};

/** モジュールスコープ + memo (HierarchyRow と同じ理由、react-arborist の可視行リマウント対策)。 */
const TaxonomyRow = memo(function TaxonomyRow({ node, style, faceCountByBusinessFunction, onChangeParent }: TaxonomyRowProps) {
  const item = node.data;
  const faceCount = faceCountByBusinessFunction?.get(item.id);
  return (
    <div style={{ ...style, paddingLeft: computeIndent(node.level) }} className="flex items-center gap-1.5 px-1">
      {node.isInternal ? (
        <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); node.toggle(); }}
          className="flex items-center justify-center min-h-[44px] min-w-[44px] text-neutral-sub shrink-0" aria-label={node.isOpen ? '折り畳む' : '開く'}>
          {node.isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
      ) : <span className="min-w-[44px] shrink-0" />}
      <span data-tree-label className="text-sm text-text-body truncate min-w-[96px]">{item.label}</span>
      <span className="text-[11px] font-mono text-text-sub shrink-0">{item.code}</span>
      {item.parent_id && !item.is_required && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border shrink-0 whitespace-nowrap">任意</span>
      )}
      {faceCountByBusinessFunction && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border shrink-0 whitespace-nowrap min-w-[112px] text-center">
          面：{faceCount ?? 0}件
        </span>
      )}
      <span className="flex-1" />
      <button type="button" tabIndex={-1} aria-label="親を変更" title="親を変更" onClick={() => onChangeParent(item)}
        className="flex items-center justify-center min-h-[44px] min-w-[44px] shrink-0 text-neutral-sub hover:text-text-main">
        <Move size={16} />
      </button>
    </div>
  );
});

/** ⚠️ react-arborist の RowContainer は `role="treeitem"`/`aria-level`/`aria-selected`/
 *  `aria-expanded`/`tabIndex:-1` を rowAttrs で自動付与する (dist/module/components/row-container.js)。
 *  自分で aria-level を付けると二重付与で無効な ARIA になるため、不足している
 *  `aria-posinset`/`aria-setsize` の2つだけをここで合成する (DefaultRow の attrs/innerRef を保持)。 */
function TaxonomyTreeRow({ node, innerRef, attrs, children }: RowRendererProps<TreeNode>) {
  const posinset = node.childIndex + 1;
  const setsize = node.parent?.children?.length ?? 1;
  return (
    <div {...attrs} ref={innerRef} onFocus={(e) => e.stopPropagation()} onClick={node.handleClick}
      aria-posinset={posinset} aria-setsize={setsize}>
      {children}
    </div>
  );
}

// =============================================================================
// 親を変更モーダル
// =============================================================================
function ChangeParentModal({
  entityType, node, allNodes, onClose, onSuccess,
}: {
  entityType: EntityType; node: TaxonomyNode; allNodes: TaxonomyNode[];
  onClose: () => void; onSuccess: () => void;
}) {
  const [parentId, setParentId] = useState(node.parent_id ?? '');
  const [isRequired, setIsRequired] = useState(node.is_required);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);

  const excluded = useMemo(() => collectSubtreeIds(allNodes, node.id), [allNodes, node.id]);
  const candidates = useMemo(
    () => flattenForSelect(allNodes).filter((f) => !excluded.has(f.node.id)),
    [allNodes, excluded],
  );

  const mut = useMutation({
    mutationFn: (confirm: boolean) => apiFetch<{ warnings: Array<{ code: string; child_business_function_id?: string; parent_business_function_id?: string }> }>(
      `/masters/taxonomy/${entityType}/${node.id}/parent`,
      {
        method: 'PUT',
        body: JSON.stringify({
          parent_id: parentId || null, is_required: isRequired, reason: reason.trim() || null,
          if_match_effective_from: node.edge_effective_from, ...(confirm ? { confirm: true } : {}),
        }),
      },
    ),
    onSuccess: (res) => {
      setConfirming(false);
      onSuccess();
      const emptied = res.warnings.filter((w) => w.code === 'target_emptied');
      const consistency = res.warnings.find((w) => w.code === 'consistency_violation');
      if (emptied.length > 0) toast.error(`この付け替えで必要スキルの要求が無くなる人材像が${emptied.length}件あります (給与の加算にも影響します)`);
      if (consistency) toast.error('この付け替えは業務の階層と矛盾しています');
      if (emptied.length === 0 && !consistency) toast.success('親を変更しました');
    },
    onError: (e) => {
      setConfirming(false);
      if (e instanceof ApiClientError && e.code === 'STALE_PARENT_VERSION') {
        toast.error('階層が他の操作で更新されています。画面を再読み込みしてからやり直してください');
        return;
      }
      if (e instanceof ApiClientError && e.code === 'TARGETS_WOULD_BE_EMPTIED') {
        setConfirming(true);
        return;
      }
      toast.error(e instanceof ApiClientError ? e.message : '親の変更に失敗しました');
    },
  });

  return (
    <>
      <Modal open title={`「${node.label}」の親を変更`} onClose={() => { if (!mut.isPending) onClose(); }} size="sm"
        footer={(
          <>
            <Button variant="secondary" disabled={mut.isPending} onClick={onClose}>キャンセル</Button>
            <Button variant="secondary" loading={mut.isPending} onClick={() => mut.mutate(false)}>変更する</Button>
          </>
        )}>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">新しい親</label>
            {/* ⚠️ 同一画面の他セクション (集約関数・整合モード) と同じ Select を使う (design-expert
                CRITICAL反映: ネイティブ select との混在は Nielsen H4 一貫性違反、ui-guidelines.md §7.2)。 */}
            <Select value={parentId || '__root__'} onValueChange={(v) => setParentId(v === '__root__' ? '' : v)}>
              <SelectTrigger aria-label="新しい親"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">(ルート直下)</SelectItem>
                {candidates.map((f) => <SelectItem key={f.node.id} value={f.node.id}>{f.path}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-body">
            <input type="checkbox" checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
            必須 (集約関数「必須指定の子のみを満たす」でこのノードを対象に含める)
          </label>
          <div>
            <label className="block text-sm text-text-sub mb-1">理由 (任意)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2}
              className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
          </div>
          <p className="text-xs text-text-sub">深さの上限や循環参照は保存時にサーバー側で検証されます。</p>
        </div>
      </Modal>
      <ConfirmDialog
        open={confirming}
        title="必要スキルの要求が無くなります"
        message="この付け替えで必要スキルの要求が無くなる人材像があります (給与の加算にも影響します)。それでも変更しますか？"
        confirmLabel="それでも変更する"
        loading={mut.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() => mut.mutate(true)}
      />
    </>
  );
}

// =============================================================================
// ツリー UI セクション
// =============================================================================
function TaxonomyTreeSection({ entityType, canManage }: { entityType: EntityType; canManage: boolean }) {
  const qc = useQueryClient();
  const [reparenting, setReparenting] = useState<TaxonomyNode | null>(null);

  const { data, isLoading, isError } = useQuery<TaxonomyResponse>({
    queryKey: ['competency-taxonomy', entityType],
    queryFn: () => apiFetch(`/masters/taxonomy/${entityType}`),
  });
  // 業務ツリーの行にその業務が持つ面 (スキル) の数を表示する (§5-4)。
  const { data: skills = [] } = useQuery<SkillMasterRow[]>({
    queryKey: ['skills'], queryFn: () => apiFetch('/masters/skills'), enabled: entityType === 'business_function',
  });
  const faceCountByBusinessFunction = useMemo(() => {
    if (entityType !== 'business_function') return null;
    const map = new Map<string, number>();
    for (const s of skills) {
      if (!s.business_function_id) continue;
      map.set(s.business_function_id, (map.get(s.business_function_id) ?? 0) + 1);
    }
    return map;
  }, [entityType, skills]);

  const nodes = data?.nodes ?? [];
  const tree = useMemo(() => buildTaxonomyTree(nodes), [nodes]);

  const handleChangeParent = useCallback((item: TaxonomyNode) => { if (canManage) setReparenting(item); }, [canManage]);

  const renderRow = useCallback(
    ({ node, style }: NodeRendererProps<TreeNode>) => (
      <TaxonomyRow node={node} style={style} faceCountByBusinessFunction={faceCountByBusinessFunction} onChangeParent={handleChangeParent} />
    ),
    [faceCountByBusinessFunction, handleChangeParent],
  );

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-text-body">階層ツリー</h3>
      <div className="border border-surface-border rounded-lg bg-white p-2 overflow-x-auto">
        {isLoading ? (
          <div className="p-4 text-text-sub text-sm" role="status">読み込み中…</div>
        ) : isError ? (
          <p role="alert" className="p-4 text-sm text-alert-text">階層の取得に失敗しました。</p>
        ) : tree.length === 0 ? (
          <div className="p-6 text-center text-text-sub text-sm">まだ分類が登録されていません。先にマスタでコード・名称を登録してください。</div>
        ) : (
          <div className="min-w-[480px]">
            <Tree<TreeNode>
              data={tree}
              idAccessor="id"
              childrenAccessor="children"
              openByDefault
              width="100%"
              height={480}
              rowHeight={44}
              indent={0}
              renderRow={TaxonomyTreeRow}
            >
              {renderRow}
            </Tree>
          </div>
        )}
      </div>
      {reparenting && canManage && (
        <ChangeParentModal
          entityType={entityType} node={reparenting} allNodes={nodes}
          onClose={() => setReparenting(null)}
          onSuccess={() => {
            setReparenting(null);
            qc.invalidateQueries({ queryKey: ['competency-taxonomy', entityType] });
            qc.invalidateQueries({ queryKey: ['competency-hierarchy-consistency-violations'] });
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// 意味論の設定セクション
// =============================================================================
type SemanticsDraft = { expansion_enabled: boolean; prerequisite_enabled: boolean; aggregation_function: 'all_children' | 'required_only'; max_depth: string };

function SemanticsSection({
  entityType, setting, canManage, onSaved,
}: { entityType: EntityType; setting: HierarchySetting; canManage: boolean; onSaved: () => void }) {
  const [draft, setDraft] = useState<SemanticsDraft>(() => ({
    expansion_enabled: setting.expansion_enabled, prerequisite_enabled: setting.prerequisite_enabled,
    aggregation_function: setting.aggregation_function, max_depth: String(setting.max_depth),
  }));
  const [confirming, setConfirming] = useState(false);
  // effective_from が変わった (= 保存/再取得された) タイミングでドラフトをサーバー値へ合わせ直す。
  useEffect(() => {
    setDraft({
      expansion_enabled: setting.expansion_enabled, prerequisite_enabled: setting.prerequisite_enabled,
      aggregation_function: setting.aggregation_function, max_depth: String(setting.max_depth),
    });
  }, [setting.effective_from, setting.expansion_enabled, setting.prerequisite_enabled, setting.aggregation_function, setting.max_depth]);

  const mut = useMutation({
    mutationFn: (confirm: boolean) => apiFetch<{ warnings: Array<{ code: string }> }>(`/masters/hierarchy-settings/${entityType}`, {
      method: 'PUT',
      body: JSON.stringify({
        expected_effective_from: setting.effective_from,
        expansion_enabled: draft.expansion_enabled,
        ...(entityType === 'skill' ? { prerequisite_enabled: draft.prerequisite_enabled } : {}),
        aggregation_function: draft.aggregation_function,
        max_depth: Number(draft.max_depth) || 1,
        ...(confirm ? { confirm: true } : {}),
      }),
    }),
    onSuccess: (res) => {
      setConfirming(false);
      onSaved();
      if (res.warnings.length > 0) toast.error(`この変更で必要スキルの要求が無くなる人材像が${res.warnings.length}件あります`);
      else toast.success('保存しました');
    },
    onError: (e) => {
      setConfirming(false);
      if (e instanceof ApiClientError && e.code === 'STALE_SETTINGS_VERSION') {
        toast.error('設定が他の操作で更新されています。画面を再読み込みしてからやり直してください');
        return;
      }
      if (e instanceof ApiClientError && e.code === 'TARGETS_WOULD_BE_EMPTIED') {
        setConfirming(true);
        return;
      }
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    },
  });

  const maxDepthInvalid = !Number.isInteger(Number(draft.max_depth)) || Number(draft.max_depth) < 1 || Number(draft.max_depth) > MAX_TAXONOMY_DEPTH;

  if (entityType === 'abstraction_level') {
    return (
      <div className="border border-surface-border rounded-lg bg-white p-4">
        <div className="flex items-start gap-2" id="abstraction-level-explanation">
          <Info size={16} className="text-chrome-base shrink-0 mt-0.5" aria-hidden />
          <p className="text-sm text-text-body" aria-describedby="abstraction-level-explanation">
            抽象度は仕事の裁量度の分類であり、人の到達度を持たないため、展開・前提の対象になりません。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <h3 className="text-sm font-semibold text-text-body">意味論の設定</h3>
      {entityType === 'business_function' && (
        <div className="flex items-start gap-2" id="business-function-explanation">
          <Info size={16} className="text-chrome-base shrink-0 mt-0.5" aria-hidden />
          <p className="text-xs text-text-sub" aria-describedby="business-function-explanation">
            業務そのものへの認定は存在しないため、前提 (親の認定が無ければ子を確定できない) はスキルにのみ設定できます。
          </p>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-text-body">
        <input type="checkbox" disabled={!canManage} checked={draft.expansion_enabled}
          onChange={(e) => setDraft({ ...draft, expansion_enabled: e.target.checked })} />
        展開を有効にする (親ノードを人材像の必要スキルに指定できるようにする)
      </label>
      {entityType === 'skill' && (
        <label className="flex items-center gap-2 text-sm text-text-body">
          <input type="checkbox" disabled={!canManage} checked={draft.prerequisite_enabled}
            onChange={(e) => setDraft({ ...draft, prerequisite_enabled: e.target.checked })} />
          前提を有効にする (親スキルの確定が無ければ子スキルを確定できないようにする)
        </label>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm text-text-sub mb-1">最大階層数 (1〜{MAX_TAXONOMY_DEPTH})</label>
          <input type="number" min={1} max={MAX_TAXONOMY_DEPTH} disabled={!canManage} value={draft.max_depth}
            aria-invalid={maxDepthInvalid}
            onChange={(e) => setDraft({ ...draft, max_depth: e.target.value })}
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring" />
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">集約関数</label>
          <Select value={draft.aggregation_function} disabled={!canManage}
            onValueChange={(v) => setDraft({ ...draft, aggregation_function: v as 'all_children' | 'required_only' })}>
            <SelectTrigger aria-label="集約関数"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all_children">{AGGREGATION_LABELS.all_children}</SelectItem>
              <SelectItem value="required_only">{AGGREGATION_LABELS.required_only}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {canManage && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" disabled={maxDepthInvalid || mut.isPending} loading={mut.isPending} onClick={() => mut.mutate(false)}>保存</Button>
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        title="必要スキルの要求が無くなります"
        message="この変更で必要スキルの要求が無くなる人材像があります (給与の加算にも影響します)。それでも変更しますか？"
        confirmLabel="それでも変更する"
        loading={mut.isPending}
        onClose={() => setConfirming(false)}
        onConfirm={() => mut.mutate(true)}
      />
    </div>
  );
}

// =============================================================================
// 給与算入フラグ (D-788、業務/スキルのみ、別エンドポイント・別権限)
// =============================================================================
function PaySection({ entityType, setting, onSaved }: { entityType: EntityType; setting: HierarchySetting; onSaved: () => void }) {
  const user = useAuthStore((s) => s.user);
  const canManagePay = hasTenantWidePermission(user?.effective_permissions, 'compensation', 'manage');
  const { data: features = [] } = useQuery<TenantFeature[]>({ queryKey: ['tenant-features'], queryFn: () => apiFetch('/tenant-features') });
  const payFlagEnabled = features.some((f) => f.feature_code === 'compensation_policy_link' && f.is_enabled);

  const [checked, setChecked] = useState(setting.pay_increment_includes_group_targets);
  useEffect(() => setChecked(setting.pay_increment_includes_group_targets), [setting.effective_from, setting.pay_increment_includes_group_targets]);

  const mut = useMutation({
    mutationFn: () => apiFetch(`/compensation-settings/competency-hierarchy-pay/${entityType}`, {
      method: 'PUT',
      body: JSON.stringify({ expected_effective_from: setting.effective_from, pay_increment_includes_group_targets: checked }),
    }),
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => {
      if (e instanceof ApiClientError && e.code === 'STALE_SETTINGS_VERSION') {
        toast.error('設定が他の操作で更新されています。画面を再読み込みしてからやり直してください');
        return;
      }
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    },
  });

  // 権限もフラグも持たないユーザーには操作不能な項目を見せない (機能を持たないユーザーへの表示を避ける)。
  if (!canManagePay || !payFlagEnabled) return null;

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <h3 className="text-sm font-semibold text-text-body">給与算入フラグ</h3>
      <p className="text-xs text-text-sub">
        親ノードの展開で満たされた要求 (「くくり」経由の必要スキル) を、給与の昇給加算に含めるかどうかを設定します。
        他の階層設定とは別エンドポイント・別権限で保護されており、誤って一括保存されることはありません。
      </p>
      <label className="flex items-center gap-2 text-sm text-text-body">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        親ノードの展開による充足を給与の昇給加算に含める
      </label>
      {checked && (
        <div className="flex items-start gap-2 p-2 rounded bg-alert-bg border border-alert-border/40">
          <AlertTriangle size={14} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
          <p className="text-xs text-alert-text">この設定は給与の加算に影響します。</p>
        </div>
      )}
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" disabled={mut.isPending} loading={mut.isPending} onClick={() => mut.mutate()}>保存</Button>
      </div>
    </div>
  );
}

// =============================================================================
// 整合設定セクション (D-731/D-742、entity_type を持たないテナント単位の設定。3タブ共通で1箇所)
// =============================================================================
function ConsistencySection({
  mode, effectiveFrom, canManage, onSaved,
}: { mode: 'warn' | 'enforce'; effectiveFrom: string | null; canManage: boolean; onSaved: () => void }) {
  const [draftMode, setDraftMode] = useState<'warn' | 'enforce'>(mode);
  const [violationsOpen, setViolationsOpen] = useState(false);
  useEffect(() => setDraftMode(mode), [mode]);

  const { data: violationsData } = useQuery<{ violations: ConsistencyViolation[] }>({
    queryKey: ['competency-hierarchy-consistency-violations'],
    queryFn: () => apiFetch('/masters/hierarchy-consistency-violations'),
  });
  const violations = violationsData?.violations ?? [];

  const mut = useMutation({
    mutationFn: (nextMode: 'warn' | 'enforce') => apiFetch('/masters/hierarchy-consistency-mode', {
      method: 'PUT', body: JSON.stringify({ mode: nextMode, expected_effective_from: effectiveFrom }),
    }),
    onSuccess: () => { onSaved(); toast.success('保存しました'); },
    onError: (e) => {
      if (e instanceof ApiClientError && e.code === 'STALE_SETTINGS_VERSION') {
        toast.error('設定が他の操作で更新されています。画面を再読み込みしてからやり直してください');
        return;
      }
      if (e instanceof ApiClientError && e.code === 'CONSISTENCY_VIOLATIONS_EXIST') {
        setViolationsOpen(true);
        toast.error('既存の階層に整合違反があるため、強制モードへ切り替えられません。違反を先に解消してください');
        return;
      }
      toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました');
    },
  });

  return (
    <div className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-text-body">階層の整合チェック</h3>
        {violations.length > 0 && (
          <button type="button" onClick={() => setViolationsOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-alert-text underline underline-offset-2 min-h-[44px]">
            <AlertTriangle size={14} aria-hidden />違反 {violations.length}件を見る
          </button>
        )}
      </div>
      <p className="text-xs text-text-sub">親スキルの業務は子スキルの業務の祖先または同一である必要があります。違反時の扱いを選べます。</p>
      {violations.length > 0 && (
        <div className="flex items-start gap-2 p-2 rounded bg-alert-bg border border-alert-border/40">
          <AlertTriangle size={14} className="text-alert-text shrink-0 mt-0.5" aria-hidden />
          <p className="text-xs text-alert-text">業務の階層と矛盾するスキルの親子関係が {violations.length} 件あります。</p>
        </div>
      )}
      <div className="max-w-xs">
        <Select value={draftMode} disabled={!canManage} onValueChange={(v) => setDraftMode(v as 'warn' | 'enforce')}>
          <SelectTrigger aria-label="整合チェックの動作"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="warn">警告のみ (保存は継続できる)</SelectItem>
            <SelectItem value="enforce">禁止する (矛盾する変更を保存できなくする)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {canManage && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" disabled={mut.isPending || draftMode === mode} loading={mut.isPending} onClick={() => mut.mutate(draftMode)}>保存</Button>
        </div>
      )}

      <Modal open={violationsOpen} title="整合違反の一覧" onClose={() => setViolationsOpen(false)} size="md">
        {violations.length === 0 ? (
          <p className="text-sm text-text-sub">違反はありません。</p>
        ) : (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {violations.map((v) => (
              <li key={`${v.child_skill_id}-${v.parent_skill_id}`} className="border border-surface-border rounded p-2 text-sm">
                <p className="text-text-body break-words">
                  子スキル「{v.child_skill_label}」({v.child_business_function_label}) の親スキル「{v.parent_skill_label}」の業務は
                  「{v.parent_business_function_label}」で、子の業務の祖先ではありません。
                </p>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}

// =============================================================================
// entity_type ごとのタブ本体
// =============================================================================
function EntityTypeTab({
  entityType, setting, canManage, onSaved,
}: { entityType: EntityType; setting: HierarchySetting; canManage: boolean; onSaved: () => void }) {
  return (
    <div className="space-y-3">
      <SemanticsSection entityType={entityType} setting={setting} canManage={canManage} onSaved={onSaved} />
      {entityType !== 'abstraction_level' && <PaySection entityType={entityType} setting={setting} onSaved={onSaved} />}
      <TaxonomyTreeSection entityType={entityType} canManage={canManage} />
    </div>
  );
}

export function CompetencyHierarchySettingsPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'skill', 'manage');
  const [tab, setTab] = useState<EntityType>('business_function');
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<HierarchySettingsResponse>({
    queryKey: ['competency-hierarchy-settings'],
    queryFn: () => apiFetch('/masters/hierarchy-settings'),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['competency-hierarchy-settings'] });
    qc.invalidateQueries({ queryKey: ['competency-hierarchy-consistency-violations'] });
    qc.invalidateQueries({ queryKey: ['competency-taxonomy'] });
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">能力の階層</h2>
        <p className="text-sm text-text-sub mt-1">業務・スキル・抽象度の親子関係を設定します。設定した階層は人材像の必要スキルの「くくり」指定に使えます。</p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です (登録・編集には全店舗管理権限が必要です)。</p>}
      </div>

      {isLoading ? (
        <div className="text-text-sub" role="status">読み込み中…</div>
      ) : isError || !data ? (
        <EmptyState message="設定の取得に失敗しました" />
      ) : (
        <>
          <ConsistencySection
            mode={data.consistency_mode} effectiveFrom={data.consistency_mode_effective_from}
            canManage={canManage} onSaved={invalidateAll}
          />

          <div className="border-b border-surface-border overflow-x-auto">
            <nav className="flex gap-1 min-w-max" aria-label="能力の階層タブ">
              {ENTITY_TABS.map(({ key, label }) => (
                <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key}
                  className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                    tab === key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
                  }`}>
                  {label}
                </button>
              ))}
            </nav>
          </div>

          {(() => {
            const setting = data.settings.find((s) => s.entity_type === tab);
            if (!setting) return null;
            // key={tab}: タブ切替時にツリーの「親を変更」モーダル等の内部 state を確実にリセットする
            // (同一コンポーネントインスタンスのまま entity_type だけ変わると古い対象ノードが残りうる)。
            return <EntityTypeTab key={tab} entityType={tab} setting={setting} canManage={canManage} onSaved={invalidateAll} />;
          })()}
        </>
      )}
    </div>
  );
}
